/**
 * useConfirmOrder — confirming an order, straight from its stored data.
 *
 * The old flow mounted a hidden copy of the Edit Order form, waited for ~10
 * background queries and then submitted it. This hook replaces that entirely:
 * it reads the `orders` row and its `legs` rows, decides per leg what can be
 * confirmed (`confirm-plan`), and runs the confirmation (`confirm-execute`)
 * with Sync-then-Persist ordering, rollback and a hard 15s cap on any single
 * external dispatch call.
 *
 * All the rules live in pure modules; this hook only supplies data and ports,
 * which keeps it thin and leaves the decision logic unit-testable.
 */
import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTenant } from "@/hooks/useTenant";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import { resolveLegStatus } from "@/lib/leg-status-resolve";
import { useTruckEligibilityMap } from "@/hooks/useTruckEligibility";
import { useTractorOosStatusMap, computeOosBlockedIdsForWindow } from "@/hooks/useTractorOosStatusMap";
import {
  buildConfirmPlan,
  overriddenReasons as computeOverriddenReasons,
  type ConfirmPlan,
  type ConfirmPlanLeg,
} from "@/lib/confirm-plan";
import { executeConfirm, type ConfirmLegTarget } from "@/lib/confirm-execute";
import { buildConfirmTripRequest } from "@/lib/confirm-trip-payload";
import { createEapiTripChecked } from "@/lib/eapi-create-trip";
import { buildCustomFieldsForOrder } from "@/lib/eapi-custom-fields";
import { bobtailPredicate } from "@/lib/confirm-readiness";
import { insertActiveLegTrip } from "@/lib/leg-trip-sync";
import {
  mapConfirmPlanLegs,
  legMirrorsParentEquipment,
  type ConfirmPlanLegSource,
  type ConfirmPlanOrderSource,
} from "@/lib/confirm-plan-legs";

import { buildConfirmDocsOverrideEvent } from "@/lib/confirm-docs-override-event";
import { recordChangeEvents } from "@/hooks/useChangeEventPrompt";
import type { EapiCreateTripBody } from "@/lib/eapi-trip-mapper";
import type { Location } from "@/types/tms";

export interface UseConfirmOrderArgs {
  /** The parent `orders` row. */
  order: any | null | undefined;
  /** Its `legs` rows, ordered by `leg_sequence`. */
  legs: any[] | undefined;
  /** True while the legs are being loaded for the first time. */
  legsPending?: boolean;
  /** True when the legs query failed — distinct from "this order has none". */
  legsError?: boolean;
  /** Order-level documentation blockers, already resolved by the caller. */
  documentBlockers?: string[];
  /** Admin acknowledged the documentation warning. */
  documentOverride?: boolean;
  /** Caller is an admin: the acknowledged override also waives truck
   *  eligibility and out-of-service blocks (never missing equipment). */
  isAdmin?: boolean;
  userEmail?: string | null;
  onConfirmed?: () => void;
}

export function useConfirmOrder({
  order,
  legs,
  legsPending,
  legsError,
  documentBlockers,
  documentOverride,
  isAdmin,
  userEmail,
  onConfirmed,
}: UseConfirmOrderArgs) {
  const queryClient = useQueryClient();
  const { tenantId, activeTenant } = useTenant();
  const { eapiClient, isReady: eapiReady, environment: eapiEnvironment } = useEapiAuth();
  const { data: trailerTypes } = useTrailerTypes();
  const eligibility = useTruckEligibilityMap();
  const oos = useTractorOosStatusMap();
  const [pendingLegIds, setPendingLegIds] = useState<string[]>([]);

  const { data: locations } = useQuery({
    queryKey: ["locations", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("archived", false);
      if (error) throw error;
      return (data ?? []) as unknown as Location[];
    },
    staleTime: 5 * 60_000,
    enabled: !!tenantId,
  });

  const trailerTypeById = useMemo(
    () => new Map((trailerTypes ?? []).map((t) => [t.id, t])),
    [trailerTypes],
  );

  /** Out-of-service trucks, evaluated against each leg's own window. */
  const oosBlockedByLegId = useMemo(() => {
    const map = new Map<string, ReadonlySet<string>>();
    for (const leg of legs ?? []) {
      map.set(
        leg.id,
        computeOosBlockedIdsForWindow(
          oos.recordsById,
          leg.ready_time,
          leg.requested_delivery_time_end || leg.requested_delivery_time,
        ),
      );
    }
    return map;
  }, [legs, oos.recordsById]);

  const planLegs: ConfirmPlanLeg[] = useMemo(
    () =>
      // Multi-leg rules (parent equipment mirrors leg 1 only; trip type falls
      // back on every leg) live in `@/lib/confirm-plan-legs` so they are
      // unit-tested — see confirm-plan-legs.test.ts.
      mapConfirmPlanLegs(
        (legs ?? []) as unknown as ConfirmPlanLegSource[],
        order as unknown as ConfirmPlanOrderSource,
        (leg, isMultiLeg) =>
          resolveLegStatus(
            {
              driver_status: leg.driver_status as string | null,
              eapi_status: leg.eapi_status ?? null,
            },
            order?.status ?? null,
            isMultiLeg,
          ) ?? null,
      ) as ConfirmPlanLeg[],
    [legs, order],
  );



  const buildPlan = useCallback(
    (override: boolean): ConfirmPlan =>
      buildConfirmPlan({
        orderStatus: order?.status,
        legs: planLegs,
        isBobtailTrailerType: bobtailPredicate(trailerTypes),
        eligibilityByTractor: eligibility.eligibilityByTractor,
        eligibilityGateState: eligibility.gateState,
        oosBlockedByLegId,
        documentBlockers,
        documentOverride: override,
        adminOverride: override && !!isAdmin,
        legsLoadState: legsPending ? "loading" : legsError ? "error" : "ready",
      }),
    [
      order?.status,
      planLegs,
      trailerTypes,
      eligibility.eligibilityByTractor,
      eligibility.gateState,
      oosBlockedByLegId,
      documentBlockers,
      legsPending,
      legsError,
      isAdmin,
    ],
  );

  const plan = useMemo(() => buildPlan(!!documentOverride), [buildPlan, documentOverride]);
  /** The same plan with the documentation gate waived — used only after an
   *  admin acknowledges the missing-documents warning. */
  const overridePlan = useMemo(() => buildPlan(true), [buildPlan]);
  /** What the override waives, measured against the un-overridden plan. */
  const basePlan = useMemo(() => buildPlan(false), [buildPlan]);
  const overriddenReasons = useMemo(
    () => computeOverriddenReasons(basePlan, overridePlan),
    [basePlan, overridePlan],
  );

  /** True while the data behind the decision is still unknown. */
  const isLoading = !!legsPending || eligibility.gateState === "loading";

  const mutation = useMutation({
    mutationFn: async (legIds: string[]) => {
      if (!order?.id) throw new Error("This order isn't loaded yet.");
      if (!tenantId) throw new Error("No workspace is selected.");
      const selected = (legs ?? []).filter((l) => legIds.includes(l.id));
      if (selected.length === 0) throw new Error("No legs were selected to confirm.");

      const targets: ConfirmLegTarget[] = [];
      const legCount = (legs ?? []).length;
      for (const leg of selected) {
        // Same rule the readiness check uses: the order's trailer columns
        // mirror leg 1, so legs 2..N must not borrow them when building the
        // dispatch payload. Trip type is not equipment and falls back always.
        const mirrorsParent = legMirrorsParentEquipment(leg.leg_sequence, legCount);
        const trailerTypeId =
          leg.trailer_type_id ?? (mirrorsParent ? order.trailer_type_id ?? null : null);
        const tt = trailerTypeId ? trailerTypeById.get(trailerTypeId) : null;
        const tripType = leg.trip_type ?? order.trip_type ?? null;

        let customFields: Awaited<ReturnType<typeof buildCustomFieldsForOrder>> | undefined;
        if (eapiClient && eapiReady) {
          customFields = await buildCustomFieldsForOrder(eapiClient, eapiEnvironment, {
            customerShortCode: order.billing_party?.short_code,
            trailerId:
              leg.trailer_id ?? (mirrorsParent ? order.trailer_id ?? null : null),
            operator1Email: leg.assigned_operator?.email ?? null,
            operator2Email: leg.assigned_operator_2?.email ?? null,
            tripType,
          });
        }


        targets.push({
          legId: leg.id,
          legSequence: leg.leg_sequence,
          tripRequest: buildConfirmTripRequest({
            leg,
            parentUniqueId: order.unique_id,
            tractor: leg.assigned_tractor ?? null,
            trailerType: tt ? { kind: tt.kind, length_ft: tt.length_ft } : null,
            tripType,
            locations,
            customFields,
            tenantSlug: activeTenant,
            legCount,
          }),
        });
      }

      const needsEapi = targets.some((t) => t.tripRequest !== null);
      if (needsEapi && (!eapiClient || !eapiReady)) {
        throw new Error("The dispatch connection isn't ready yet. Try again in a moment.");
      }

      return executeConfirm(targets, {
        createTrip: (req) =>
          createEapiTripChecked(eapiClient!, req.tripBody as EapiCreateTripBody, {
            label: req.label,
            assert: req.assert as any,
          }),
        cancelTrip: (tripId, reason) => eapiClient!.cancelTrip(tripId, reason),
        persistLegs: async (rows) => {
          const { data: authData } = await supabase.auth.getUser();
          const userId = authData?.user?.id ?? null;
          for (const row of rows) {
            const { error } = await supabase
              .from("legs")
              .update({ driver_status: "confirmed" })
              .eq("id", row.legId);
            if (error) throw error;
            if (row.eapiTripId) {
              await insertActiveLegTrip({
                legId: row.legId,
                tenantId,
                eapiTripId: row.eapiTripId,
              });
              // `orders.eapi_trip_id` stays a leg-1 compatibility mirror.
              if (row.legSequence === 1) {
                await supabase
                  .from("orders")
                  .update({ eapi_trip_id: row.eapiTripId })
                  .eq("id", order.id);
              }
            }
          }
          void userId;
        },
        promoteParent: async () => {
          const { data: authData } = await supabase.auth.getUser();
          const patch: Record<string, unknown> = { status: "confirmed" };
          if (!order.confirmed_by_user_id && authData?.user?.id) {
            patch.confirmed_by_user_id = authData.user.id;
            patch.confirmed_at = new Date().toISOString();
          }
          const { error } = await supabase.from("orders").update(patch).eq("id", order.id);
          if (error) throw error;
        },
        logConfirmation: async (rows) => {
          await supabase.from("order_change_log").insert(
            rows.map((row) => ({
              order_id: order.id,
              changed_by: userEmail || "Unknown",
              ...(tenantId ? { tenant_id: tenantId } : {}),
              field_name: `leg_${row.legSequence}_status`,
              old_value: "booked",
              new_value: "confirmed",
            })),
          );
        },
        legStatusesAfter: (confirmedLegIds) =>
          (legs ?? []).map((l) =>
            confirmedLegIds.includes(l.id) ? "confirmed" : (l.driver_status as string),
          ),
      });
    },
    onMutate: (legIds: string[]) => setPendingLegIds(legIds),
    onSettled: () => setPendingLegIds([]),
    onSuccess: (result) => {
      toast.success(
        result.parentPromoted
          ? "Order confirmed."
          : `${result.confirmedLegIds.length} leg${result.confirmedLegIds.length > 1 ? "s" : ""} confirmed.`,
      );
      queryClient.invalidateQueries({ queryKey: ["order", order?.id] });
      queryClient.invalidateQueries({ queryKey: ["order-children-legs", order?.id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      // Traceable record of an admin confirming without the billing party's
      // required documentation on file. Best-effort — never fails the confirm.
      const waived = overriddenReasons.length > 0 ? overriddenReasons : documentBlockers ?? [];
      if (documentOverride && tenantId && waived.length > 0) {
        const evt = buildConfirmDocsOverrideEvent({
          orderId: order?.id,
          tenantId,
          eapiTripId: order?.eapi_trip_id ?? null,
          ack: {
            missing: [...waived],
            customerName: order?.billing_party?.organization_name ?? null,
          },
          previousStatus: order?.status ?? null,
        });
        if (evt) {
          void recordChangeEvents([evt]).then((r) => {
            if (r.error) {
              console.warn("[useConfirmOrder] confirm-docs override log failed", r.error);
            }
          });
        }
      }
      onConfirmed?.();
    },
    onError: (err: any) => {
      toast.error(err?.message || "Couldn't confirm this order. Please try again.");
    },
  });

  const confirmLegs = useCallback(
    (legIds: string[]) => mutation.mutate(legIds),
    [mutation],
  );
  const confirmAllEligible = useCallback(
    () => mutation.mutate(plan.eligibleLegIds),
    [mutation, plan.eligibleLegIds],
  );

  return {
    plan,
    overridePlan,
    overriddenReasons,
    isLoading,
    isPending: mutation.isPending,
    pendingLegIds,
    confirmLegs,
    confirmAllEligible,
  };
}
